import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API from '../api/axios';

export default function NoteEditor({ noteId, folderId, onBack }) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(folderId);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(!!noteId);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveFolderId, setMoveFolderId] = useState(null);

  useEffect(() => {
    const fetchTags = async () => {
      const { data } = await API.get('/tags');
      setTags(data);
    };
    const fetchFolders = async () => {
      const { data } = await API.get('/folders');
      setFolders(data);
    };
    fetchTags();
    fetchFolders();

    if (noteId) {
      const fetchNote = async () => {
        const { data } = await API.get(`/notes/${noteId}`);
        setName(data.name);
        setContent(data.content || '');
        setSelectedTagIds(data.tags ? data.tags.map((t) => t.id) : []);
        setCurrentFolderId(data.folder_id);
      };
      fetchNote();
    }
  }, [noteId]);

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (noteId) {
        await API.put(`/notes/${noteId}`, { name: name.trim(), content, folder_id: currentFolderId, tag_ids: selectedTagIds });
      } else {
        await API.post('/notes', { folder_id: currentFolderId, name: name.trim(), content, tag_ids: selectedTagIds });
      }
      onBack();
    } catch {
      setSaving(false);
    }
  };

  const buildFolderOptions = (items, parentId = null, depth = 0) => {
    return items
      .filter((f) => f.parent_id === parentId)
      .flatMap((f) => [
        { id: f.id, name: f.name, label: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0'.repeat(depth) + f.name },
        ...buildFolderOptions(items, f.id, depth + 1),
      ]);
  };
  const folderOptions = buildFolderOptions(folders);
  const currentFolderName = folderOptions.find((f) => f.id === currentFolderId)?.name || '';

  const handleMove = () => {
    setMoveFolderId(currentFolderId);
    setShowMoveModal(true);
  };

  const handleMoveConfirm = async () => {
    setCurrentFolderId(moveFolderId);
    setShowMoveModal(false);
    if (noteId) {
      await API.put(`/notes/${noteId}`, { name: name.trim(), content, folder_id: moveFolderId, tag_ids: selectedTagIds });
    }
  };

  return (
    <div className="p-3 d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <button className="btn btn-sm btn-outline-secondary me-2" onClick={onBack}>
            &larr; Back
          </button>
          <h5 className="mb-0">{noteId ? 'Edit Note' : 'New Note'}</h5>
        </div>
        <div className="d-flex align-items-center">
          {noteId && (
            <span className="text-muted me-2 small">
              {currentFolderName}
              <button className="btn btn-sm btn-outline-secondary ms-2" onClick={handleMove}>Move</button>
            </span>
          )}
          <button
            className={`btn btn-sm me-2 ${preview ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => setPreview(!preview)}
          >
            {preview ? 'Edit' : 'Preview'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <input
          className="form-control form-control-lg"
          placeholder="Note name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      {tags.length > 0 && (
        <div className="mb-3 d-flex flex-wrap gap-2">
          {tags.map((tag) => {
            const selected = selectedTagIds.includes(tag.id);
            return (
              <span
                key={tag.id}
                className="badge"
                style={{
                  backgroundColor: selected ? tag.color : 'transparent',
                  color: selected ? '#fff' : tag.color,
                  border: `2px solid ${tag.color}`,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        {preview ? (
          <div className="border rounded p-3 h-100 overflow-auto" style={{ fontSize: '14px' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </div>
        ) : (
          <textarea
            className="form-control h-100"
            style={{ fontFamily: 'monospace', resize: 'none', fontSize: '14px' }}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your note in markdown..."
          />
        )}
      </div>

      {showMoveModal && (
        <MoveModal
          folderOptions={folderOptions}
          selectedFolderId={moveFolderId}
          onSelect={setMoveFolderId}
          onConfirm={handleMoveConfirm}
          onClose={() => setShowMoveModal(false)}
        />
      )}
    </div>
  );
}

function MoveModal({ folderOptions, selectedFolderId, onSelect, onConfirm, onClose }) {
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selectedName = folderOptions.find((f) => f.id === selectedFolderId)?.name || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Move Note</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <label className="form-label">Destination Folder</label>
            <div ref={dropdownRef}>
              <div
                className="form-select"
                style={{ cursor: 'pointer' }}
                onClick={() => setOpen(!open)}
              >
                {selectedName || 'Select folder'}
              </div>
              {open && (
                <ul className="dropdown-menu show w-100" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {folderOptions.map((f) => (
                    <li key={f.id}>
                      <button
                        type="button"
                        className={`dropdown-item${f.id === selectedFolderId ? ' active' : ''}`}
                        onClick={() => {
                          onSelect(f.id);
                          setOpen(false);
                        }}
                        dangerouslySetInnerHTML={{ __html: f.label.replace(/\u00A0/g, '&nbsp;') }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={onConfirm}>Move</button>
          </div>
        </div>
      </div>
    </div>
  );
}
